import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { imprimirCupomCaixa } from "../../../utils/cupomCaixa";
import { caixaApi } from "../api/caixaApi";
import { AUTOMATICAS, SEM_FORMA, paraCupom } from "../lib/caixa";
import { emAbertoDoErro, temEmAberto } from "../lib/emAberto";

const NADA_EM_ABERTO = { pedidos: [], mesas: [] };

/**
 * Estado do fechamento. Carrega uma vez por abertura do popup — antes o
 * Dashboard recriava o objeto do caixa a cada 30 s e o popup zerava a
 * contagem no meio (achado do levantamento de 24/09).
 *
 * `emAberto` (22/10/2026): pedidos e mesas em aberto na loja inteira. Com
 * qualquer um, o Conferir fica travado — e o servidor recusa o fechar com a
 * mesma lista (409), que a tela reaproveita se chegou por fora.
 */
export default function useFechamento({ open, caixaId, onFechado }) {
  const [dados, setDados] = useState({ resumo: null, movimentos: [], emAberto: NADA_EM_ABERTO, emAbertoPronto: false });
  const [contado, setContado] = useState({});
  const [resultado, setResultado] = useState(null);
  const [justificativa, setJustificativa] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const carregado = useRef(null);

  useEffect(() => {
    if (!open) { carregado.current = null; return; }
    if (!caixaId || carregado.current === caixaId) return;
    carregado.current = caixaId;
    setContado({}); setResultado(null); setJustificativa("");
    setDados({ resumo: null, movimentos: [], emAberto: NADA_EM_ABERTO, emAbertoPronto: false });
    // Em aberto vem à parte: o aviso aparece antes do resumo (que recalcula o caixa e demora).
    // A contagem só aparece depois dessa resposta: com algo em aberto ela nem aparece.
    // Se a consulta falhar, segue para a contagem — o servidor recusa o fechar do mesmo jeito.
    caixaApi.emAberto()
      .then((emAberto) => setDados((d) => ({ ...d, emAberto, emAbertoPronto: true })))
      .catch(() => setDados((d) => ({ ...d, emAbertoPronto: true })));
    Promise.all([caixaApi.obterResumo(caixaId), caixaApi.listarMovimentos(caixaId)])
      .then(([resumo, movimentos]) => setDados((d) => ({ ...d, resumo, movimentos })))
      .catch(() => toast.error("Erro ao carregar dados do caixa"));
  }, [open, caixaId]);

  // Formas que o operador conta: Dinheiro sempre; as automáticas ficam de fora.
  const formas = useMemo(() => {
    const vendas = dados.resumo?.vendas_por_pagamento || [];
    const naoConta = (v) => AUTOMATICAS.includes(v.forma_pagamento) || SEM_FORMA.includes(v.forma_pagamento);
    const outras = vendas.filter((v) => v.forma_pagamento !== "Dinheiro" && !naoConta(v));
    const din = vendas.find((v) => v.forma_pagamento === "Dinheiro");
    return {
      manuais: [{ forma: "Dinheiro", qtd: din?.qtd || 0, gaveta: true }, ...outras.map((v) => ({ forma: v.forma_pagamento || "Outros", qtd: v.qtd }))],
      automaticas: vendas.filter((v) => AUTOMATICAS.includes(v.forma_pagamento)),
      semForma: vendas.filter((v) => SEM_FORMA.includes(v.forma_pagamento)),
    };
  }, [dados.resumo]);

  const contagem = () => formas.manuais.map((f) => ({ forma: f.forma, contado: Number(contado[f.forma]) || 0 }));

  const conferir = async () => {
    setOcupado(true);
    try { setResultado(await caixaApi.conferir(caixaId, contagem())); } catch (e) { toast.error(e.message || "Não consegui conferir"); } finally { setOcupado(false); }
  };

  const fechar = async (observacao) => {
    setOcupado(true);
    try {
      const caixa = await caixaApi.fechar(caixaId, contagem(), justificativa, observacao);
      toast.success(caixa.resultado_conferencia === "conferido" ? "Caixa fechado — bateu certinho!" : "Caixa fechado com a diferença registrada");
      try { await imprimirCupomCaixa(paraCupom(caixa), dados.movimentos, dados.resumo); } catch (_) { toast.error("Caixa fechado, mas o cupom não imprimiu"); }
      onFechado?.(caixa);
      return true;
    } catch (e) {
      // Algo abriu entre a contagem e o fechar (pedido chegou, mesa foi aberta):
      // volta ao passo 1 com a lista que o servidor mandou
      const emAberto = emAbertoDoErro(e);
      if (emAberto) { setDados((d) => ({ ...d, emAberto })); setResultado(null); }
      toast.error(e.message || "Erro ao fechar caixa");
      return false;
    } finally { setOcupado(false); }
  };

  return {
    dados, formas, contado, setContado, resultado, voltar: () => setResultado(null), conferir, fechar,
    justificativa, setJustificativa, ocupado, temEmAberto: temEmAberto(dados.emAberto),
  };
}
